import { useCallback, useEffect } from 'react'

import { useTranslation } from 'react-i18n-lite'

import { useSelector } from 'react-redux'

import {
  useNavigate,
  useParams,
  NavigateFunction,
  useSearchParams
} from 'react-router-dom'

import { sendMessageToParent } from '@yes.technology/react-toolkit'

import { AuthenticationType } from 'authentication/shared/types'

import Iframe from 'shared/components/Iframe'

import { Taskmodel } from 'taskmodel/shared/types'

import useWorkflow from 'workflow/shared/hooks/useWorkflow'

import DuplicateInteractionError from './DuplicateInteractionError'

import useIframe from './hooks/useIframe'

type Params = {
  readonly workflowUuid: string
  readonly interactionUuid: string
}

const Container: React.FunctionComponent = (): JSX.Element | null => {
  const { language } = useTranslation()

  const navigate: NavigateFunction = useNavigate()

  const { workflowUuid = '', interactionUuid = '' } = useParams<Params>()

  const [searchParams] = useSearchParams()
  const hideInteractionContainer =
    searchParams.has('hideInteractionContainer') ?? false

  const urlInteraction = window.waInteractionUrl

  const token = useSelector(
    ({ authentication }: AuthenticationType) => authentication.token
  )

  const queryString = `token=${token}&lang=${language}&uuid_workflow=${workflowUuid}${
    hideInteractionContainer ? `&hideInteractionContainer=true` : ''
  }`

  const iframeSrc = `${urlInteraction}${interactionUuid}?${queryString}`

  const { workflow, refetch: refetchWorkflow } = useWorkflow({ workflowUuid })

  const { iframeAttributes, receiveMessage, isFinishedInteraction } = useIframe(
    {
      origin: urlInteraction,
      src: iframeSrc,
      title: 'Interaction',
      name: 'interaction'
    }
  )

  const taskmodels: Taskmodel[] =
    workflow?.workflowmodel_instructions_from_version.taskmodels ?? []

  const waitingInteractionFillTask = workflow?.state.tasks.find(
    ({ status }) =>
      status?.toLocaleLowerCase() ===
      window.waitingInteractionFillUUID?.toLocaleLowerCase()
  )

  const currentTaskmodelIndex = taskmodels?.findIndex(
    ({ uuid }) => uuid === waitingInteractionFillTask?.taskmodel_uuid
  )
  const currentTaskmodel = taskmodels[currentTaskmodelIndex]

  const isInteractionWaitingCompletion =
    currentTaskmodel?.interactionmodel_actionhandler?.interactionmodel_uuid ===
    interactionUuid

  const nextTaskmodelIndex = currentTaskmodelIndex + 1

  const nextAsyncTaskmodel = taskmodels
    .slice(nextTaskmodelIndex)
    .find((taskmodel) => !taskmodel?.internalmethod_actionhandler)

  const isNextTaskAnInteraction =
    !!nextAsyncTaskmodel?.interactionmodel_actionhandler

  const navigateToWorkflow = useCallback(() => {
    navigate(
      `/workflow/${workflowUuid}${hideInteractionContainer ? '?hideInteractionContainer=true' : ''}`,
      { replace: true }
    )
  }, [workflowUuid, hideInteractionContainer, navigate])

  const updateWorkflowTasks = useCallback(async () => {
    const { data } = await refetchWorkflow()
    const updatedWorkflow = data?.data?.workflow

    if (!updatedWorkflow) return

    const updatedTasks = updatedWorkflow.state.tasks
    const hasInteractionWaitingToFill = updatedTasks.some(
      ({ status }) =>
        status?.toLocaleLowerCase() ===
        window.waitingInteractionFillUUID?.toLocaleLowerCase()
    )

    hasInteractionWaitingToFill && navigateToWorkflow()
  }, [navigateToWorkflow, refetchWorkflow])

  useEffect(() => {
    if (!workflow || !isFinishedInteraction) return

    const isLastTask = !taskmodels.slice(nextTaskmodelIndex).length
    if (isLastTask) return

    updateWorkflowTasks()
  }, [
    workflow,
    isFinishedInteraction,
    taskmodels,
    nextTaskmodelIndex,
    updateWorkflowTasks
  ])

  if (isFinishedInteraction && workflow) {
    if (isNextTaskAnInteraction) {
      navigateToWorkflow()
    } else {
      sendMessageToParent(workflowUuid, interactionUuid)
    }
  }

  useEffect(() => {
    window.addEventListener('message', receiveMessage)

    return () => window.removeEventListener('message', receiveMessage)
  }, [receiveMessage])

  if (currentTaskmodel && !isInteractionWaitingCompletion) {
    return <DuplicateInteractionError />
  }

  return (
    <Iframe {...iframeAttributes} allow='camera; geolocation; microphone' />
  )
}

export default Container
